// v2 token storage — self-contained localStorage wrapper.
//
// Uses its OWN keys (ss-v2-*) so the new path never fights the legacy storage
// (auth-tokens-<env> / user-<env>). Tokens are kept in localStorage for parity
// with the legacy app. Caveat: localStorage is readable by any XSS on the page;
// this matches the current model and is a deliberate parity choice, not an
// endorsement.
//
// Shapes (mirrors what /api/v2/auth returns, same as legacy):
//   tokens: { token, refreshToken, inactivityTimeout? }
//   user:   { id, name, email, isAdmin, ... }

const TOKENS_KEY = 'ss-v2-tokens';
const USER_KEY = 'ss-v2-user';

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / unavailable — non-fatal */
  }
};

const remove = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
};

// --- tokens ---
export const getTokens = () => read(TOKENS_KEY);
export const getAccessToken = () => getTokens()?.token ?? null;
export const getRefreshToken = () => getTokens()?.refreshToken ?? null;
export const saveTokens = (tokens) => write(TOKENS_KEY, tokens);

// --- user ---
export const getUser = () => read(USER_KEY);
export const getUserId = () => getUser()?.id ?? null;
export const saveUser = (user) => write(USER_KEY, user);

// --- session ---
export const hasSession = () => getTokens() != null && getUser() != null;

// Persist a full auth payload ({ tokens, user }) from a login/refresh response.
export const saveSession = ({ tokens, user }) => {
  if (tokens) saveTokens(tokens);
  if (user) saveUser(user);
};

export const clearSession = () => {
  remove(TOKENS_KEY);
  remove(USER_KEY);
};
